import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { fetchPeriodReport, fetchEmployees, resetDemo } from '../lib/api';
import { AreaCard } from './AreaCard';
import { EmployeeDetailModal } from './EmployeeDetailModal';
import { HolidayCalendar } from './HolidayCalendar';
import { PeriodSelector } from './PeriodSelector';
import { UploadSection } from './UploadSection';
import { MappingModal } from './MappingModal';
import { EmployeesList } from './EmployeesList';
import { CompaniesPanel } from './CompaniesPanel';
import { MyPerformance } from './MyPerformance';
import {
  ViewMode,
  Period,
  getDefaultPeriodForSeededData,
} from '../lib/dateUtils';
import type { PeriodReport, EmployeeReport, SeedEmployee } from '../data/types';

type SessionRole = 'admin' | 'colaborador';

const DEFAULT_AREAS = ['Unitax', 'Corporativo', 'Tributarios', 'Procuradores', 'Fischer'];
const DEFAULT_COMPANY_AREAS: Record<'Fischer' | 'Unitax', string[]> = {
  Fischer: ['Tributarios', 'Procuradores', 'Fischer'],
  Unitax: ['Unitax'],
};

interface UploadResult {
  inserted: number;
  updated: number;
  unmatchedNames: string[];
}

export function Dashboard() {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'reportes' | 'colaboradores' | 'empresas' | 'datos'>('reportes');
  const [datosSubTab, setDatosSubTab] = useState<'actualizar' | 'dias_no_laborales'>('actualizar');
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [period, setPeriod] = useState<Period>(getDefaultPeriodForSeededData);
  const [report, setReport] = useState<PeriodReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpload, setLastUpload] = useState<{ result: UploadResult; type: string } | null>(null);
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [resetCounter, setResetCounter] = useState(0);
  const [companyAreas, setCompanyAreas] = useState<Record<'Fischer' | 'Unitax', string[]>>(DEFAULT_COMPANY_AREAS);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeReport | null>(null);

  // Toggle de sesión (demo): Admin ve toda la UI actual; Colaborador ve solo
  // su propio desempeño (mes/año acumulado), sin acceso a las demás tabs.
  const [sessionRole, setSessionRole] = useState<SessionRole>('admin');
  const [allEmployees, setAllEmployees] = useState<SeedEmployee[]>([]);
  const [selectedColaboradorId, setSelectedColaboradorId] = useState<string>('');

  useEffect(() => {
    fetchEmployees().then((emps) => {
      const active = emps.filter((e) => e.isActive).sort((a, b) => a.displayName.localeCompare(b.displayName));
      setAllEmployees(active);
    });
  }, []);

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchPeriodReport(period.startDate, period.endDate);
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleUploadComplete = useCallback((result: UploadResult, type: string) => {
    setLastUpload({ result, type });

    if (result.unmatchedNames.length > 0) {
      setIsMappingModalOpen(true);
    }

    loadReport();
  }, [loadReport]);

  const handleMappingResolved = useCallback(() => {
    loadReport();
  }, [loadReport]);

  const handleResetDemo = useCallback(() => {
    resetDemo(); // borra ediciones de localStorage, vuelve al seed
    setResetCounter((current) => current + 1); // notifica a EmployeesList que recargue
    setLastUpload(null);
    loadReport(); // recalcula el reporte con los datos originales
  }, [loadReport]);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    // Keep roughly the same period when switching
    if (mode === 'month') {
      const start = new Date(period.startDate);
      const year = start.getFullYear();
      const month = start.getMonth() + 1;
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
      setPeriod({
        startDate: firstDay.toISOString().split('T')[0],
        endDate: lastDay.toISOString().split('T')[0],
        label: `${firstDay.toLocaleDateString('es-CL', { month: 'long' })} ${year}`,
      });
    }
  }, [period]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="bg-fycom-navy text-white py-4 px-8 shadow flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Fycom Reporte Semanal</h1>
          <p className="text-slate-300 text-xs">Sistema interno de control de horas y cumplimiento</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-white/10 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setSessionRole('admin')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                sessionRole === 'admin'
                  ? 'bg-white text-fycom-navy'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Admin
            </button>
            <button
              type="button"
              onClick={() => setSessionRole('colaborador')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                sessionRole === 'colaborador'
                  ? 'bg-white text-fycom-navy'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Colaborador
            </button>
          </div>

          {sessionRole === 'colaborador' && (
            <select
              value={selectedColaboradorId}
              onChange={(e) => setSelectedColaboradorId(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm bg-white text-slate-900 outline-none"
            >
              <option value="">Selecciona un colaborador...</option>
              {allEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.displayName}
                </option>
              ))}
            </select>
          )}

          <button
            onClick={handleResetDemo}
            className="px-3 py-2 bg-slate-200 text-slate-900 rounded-lg text-sm hover:bg-slate-300 transition"
          >
            Reset demo
          </button>
          <button
            onClick={logout}
            className="text-sm text-slate-300 hover:text-white transition"
          >
            Cerrar sesión
          </button>
        </div>
      </header>

      <main className="p-8">
        <div className="max-w-none mx-auto">
          {sessionRole === 'colaborador' ? (
            !selectedColaboradorId ? (
              <div className="text-center py-12 text-slate-500">
                Selecciona un colaborador para ver su desempeño.
              </div>
            ) : (
              <MyPerformance employeeId={selectedColaboradorId} />
            )
          ) : (
            <>
          {/* Tab Navigation */}
          <div className="flex border-b border-slate-200 mb-6">
            <button
              onClick={() => setActiveTab('reportes')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition ${
                activeTab === 'reportes'
                  ? 'border-fycom-navy text-fycom-navy'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              Reportes
            </button>
            <button
              onClick={() => setActiveTab('colaboradores')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition ${
                activeTab === 'colaboradores'
                  ? 'border-fycom-navy text-fycom-navy'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              Colaboradores
            </button>
            <button
              onClick={() => setActiveTab('empresas')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition ${
                activeTab === 'empresas'
                  ? 'border-fycom-navy text-fycom-navy'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              Empresas
            </button>
            <button
              onClick={() => setActiveTab('datos')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition ${
                activeTab === 'datos'
                  ? 'border-fycom-navy text-fycom-navy'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              Datos
            </button>
          </div>

          {/* Reportes Tab */}
          {activeTab === 'reportes' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow p-6">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">Seleccionar período</h2>
                  <p className="text-slate-600 text-sm">Visualiza el cumplimiento por semana o por mes.</p>
                </div>
                <PeriodSelector
                  viewMode={viewMode}
                  period={period}
                  onViewModeChange={handleViewModeChange}
                  onPeriodChange={setPeriod}
                />
              </div>

              

              {isLoading && (
                <div className="text-center py-12 text-slate-600">Cargando reporte...</div>
              )}

              {error && (
                <div className="bg-red-50 text-fycom-red px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              {!isLoading && report && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
                  {report.areas.map((area) => (
                    <AreaCard
                      key={area.area}
                      report={area}
                      onSelectEmployee={setSelectedEmployee}
                    />
                  ))}
                </div>
              )}

              {!isLoading && report && report.areas.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No hay datos para el período seleccionado.
                </div>
              )}
            </div>
          )}

          {/* Colaboradores Tab */}
          {activeTab === 'colaboradores' && (
            <div className="bg-white rounded-xl shadow p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Gestión de Colaboradores</h2>
                <p className="text-slate-600 text-sm">Visualiza, busca y edita información de colaboradores. Puedes cambiar empresa y área.</p>
              </div>
              <EmployeesList
                defaultAreas={DEFAULT_AREAS}
                companyAreas={companyAreas}
                resetCounter={resetCounter}
              />
            </div>
          )}

          {/* Datos Tab */}
          {activeTab === 'datos' && (
            <div className="space-y-6">
              {/* Sub-tab Navigation */}
              <div className="flex gap-2">
                <button
                  onClick={() => setDatosSubTab('actualizar')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    datosSubTab === 'actualizar'
                      ? 'bg-fycom-navy text-white'
                      : 'bg-white text-slate-600 border border-slate-200 hover:text-slate-900'
                  }`}
                >
                  Actualizar Datos
                </button>
                <button
                  onClick={() => setDatosSubTab('dias_no_laborales')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    datosSubTab === 'dias_no_laborales'
                      ? 'bg-fycom-navy text-white'
                      : 'bg-white text-slate-600 border border-slate-200 hover:text-slate-900'
                  }`}
                >
                  Días No Laborales
                </button>
              </div>

              {/* Sub-tab: Actualizar Datos */}
              {datosSubTab === 'actualizar' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-xl shadow p-6">
                    <div className="mb-4">
                      <h2 className="text-lg font-semibold text-slate-900">Actualizar datos</h2>
                      <p className="text-slate-600 text-sm">Carga archivos CSV de nómina y horas.</p>
                    </div>
                    <UploadSection onUploadComplete={handleUploadComplete} />
                  </div>

                  {lastUpload && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <h3 className="font-semibold text-blue-900 mb-2">Última carga</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-blue-600">Archivo:</span>{' '}
                          <span className="font-medium">{lastUpload.type}</span>
                        </div>
                        <div>
                          <span className="text-blue-600">Insertados:</span>{' '}
                          <span className="font-medium">{lastUpload.result.inserted}</span>
                        </div>
                        <div>
                          <span className="text-blue-600">Actualizados:</span>{' '}
                          <span className="font-medium">{lastUpload.result.updated}</span>
                        </div>
                        <div>
                          <span className="text-blue-600">Nombres sin mapear:</span>{' '}
                          <span className="font-medium">{lastUpload.result.unmatchedNames.length}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Sub-tab: Días No Laborales */}
              {datosSubTab === 'dias_no_laborales' && (
                <div className="space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Días No Laborales</h2>
                    <p className="text-slate-600 text-sm">
                      Feriados legales (pre-cargados) y actividades de oficina que descuentan de la meta.
                    </p>
                  </div>
                  <HolidayCalendar onChange={loadReport} />
                </div>
              )}
            </div>
          )}

          {/* Empresas Tab */}
          {activeTab === 'empresas' && (
            <div className="bg-white rounded-xl shadow p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Empresas</h2>
                <p className="text-slate-600 text-sm">Gestiona áreas por empresa.</p>
              </div>
              <CompaniesPanel
                companyAreas={companyAreas}
                onAddArea={(company, area) => {
                  setCompanyAreas((current) => ({
                    ...current,
                    [company]: current[company].includes(area)
                      ? current[company]
                      : [...current[company], area],
                  }));
                }}
              />
            </div>
          )}
            </>
          )}
        </div>
      </main>

      <MappingModal
        isOpen={isMappingModalOpen}
        onClose={() => setIsMappingModalOpen(false)}
        onResolved={handleMappingResolved}
      />

      <EmployeeDetailModal
        employee={selectedEmployee}
        period={period}
        onClose={() => setSelectedEmployee(null)}
      />
    </div>
  );
}
